import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/layout/AppLayout";
import Sidebar from "@features/settings/components/Sidebar";
import Dashboard from "../features/dashboard/components/Dashboard";
import Loader from "../components/Utils/Loader";

/**
 * Every page except the shell and the dashboard loads on first visit rather
 * than with the app: one chunk per feature (the settings pages share one).
 * The wrapper keeps the JSX below unchanged - <Patients /> still renders
 * Patients - and shows the loader inside the layout while a chunk arrives.
 */
const lazyPage = (loader) => {
  const Page = lazy(loader);
  return function LazyPage(props) {
    return (
      <Suspense fallback={<Loader />}>
        <Page {...props} />
      </Suspense>
    );
  };
};

const Patients = lazyPage(() => import(/* webpackChunkName: "patients" */ "@features/patients/components/Patients"));
const ProgramsOverview = lazyPage(() => import(/* webpackChunkName: "programs" */ "@features/programs/components/ProgramsOverview"));
const Program = lazyPage(() => import(/* webpackChunkName: "programs" */ "@features/programs/components/Program"));
const ProgramTemplate = lazyPage(() => import(/* webpackChunkName: "programs" */ "@features/programs/components/ProgramTemplate"));
const NewProgram = lazyPage(() => import(/* webpackChunkName: "program-editor" */ "@features/programs/components/NewProgram"));
const Exercises = lazyPage(() => import(/* webpackChunkName: "exercises" */ "@features/exercises/components/Exercises"));
const Materials = lazyPage(() => import(/* webpackChunkName: "materials" */ "../features/materials/components/Materials"));
const Questionnaires = lazyPage(() => import(/* webpackChunkName: "questionnaires" */ "../features/questionnaires/components/Questionnaires"));
const Analysis = lazyPage(() => import(/* webpackChunkName: "analysis" */ "../features/analysis/components/Analysis"));
const Profile = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Profile"));
const About = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/About"));
const Locations = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Locations"));
const Tags = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Tags"));
const Favorites = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Favorites"));
const Users = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Users"));
const User = lazyPage(() => import(/* webpackChunkName: "settings" */ "../components/Permissions/User"));
const Groups = lazyPage(() => import(/* webpackChunkName: "settings" */ "../components/Permissions/Groups"));
const Permissions = lazyPage(() => import(/* webpackChunkName: "settings" */ "../components/Permissions/Permissions"));
const Generals = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Generals"));
const TwoFactor = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/TwoFactor"));
const Export = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Export"));
const Version = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Version"));
const MailArchive = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/MailArchive"));
const MailTemplates = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/MailTemplates"));
const Logging = lazyPage(() => import(/* webpackChunkName: "settings" */ "@features/settings/components/Logging"));
const Notifications = lazyPage(() => import(/* webpackChunkName: "notifications" */ "../features/notifications/components/Notifications"));
const Review = lazyPage(() => import(/* webpackChunkName: "review" */ "../features/review/components/Review"));
const Payment = lazyPage(() => import(/* webpackChunkName: "settings-payment" */ "@features/settings/components/Payment"));
const Referral = lazyPage(() => import(/* webpackChunkName: "settings-payment" */ "@features/settings/components/Referral"));

const AppRoutes = () => {
  const { t } = useTranslation();

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            <Navigate to="/dashboard" />
            <AppLayout content={<Dashboard />} />
          </>
        }
      />
      <Route
        path="/dashboard"
        element={
          <>
            <AppLayout content={<Dashboard />} />
          </>
        }
      />
      <Route
        path="/patients"
        element={
          <>
            <AppLayout content={<Patients />} />
          </>
        }
      />
      <Route
        path="/patients/:id"
        element={
          <>
            <AppLayout content={<Patients />} />
          </>
        }
      />
      <Route
        path="/programs"
        element={
          <>
            <AppLayout content={<ProgramsOverview />} />
          </>
        }
      />
      <Route
        path="/programs/:id"
        element={
          <>
            <AppLayout content={<Program />} />
          </>
        }
      />
      <Route
        path="/program_template/:id"
        element={
          <>
            <AppLayout content={<ProgramTemplate />} />
          </>
        }
      />
      <Route
        path="/programs/create"
        element={
          <>
            <AppLayout content={<NewProgram />} />
          </>
        }
      />
      <Route
        path="/exercises"
        element={
          <>
            <AppLayout content={<Exercises />} />
          </>
        }
      />
      {/* MATERIAL */}
      <Route
        path="/materials"
        element={
          <>
            <AppLayout content={<Materials />} />
          </>
        }
      />
      {/* QUESTIONNAIRES */}
      <Route
        path="/questionnaires"
        element={
          <>
            <AppLayout content={<Questionnaires />} />
          </>
        }
      />
      {/* ANALYSIS */}
      <Route
        path="/analysis"
        element={
          <>
            <AppLayout content={<Analysis />} />
          </>
        }
      />
      {/* NOTIFICATIONS */}
      <Route
        path="/notifications"
        element={
          <>
            <AppLayout content={<Notifications />} />
          </>
        }
      />
      {/* REVIEW */}
      <Route
        path="/review"
        element={
          <>
            <AppLayout content={<Review />} />
          </>
        }
      />
      {/* PREFERENCES */}
      <Route
        path="/preferences/tags"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<Tags />} title={t("settings.tags")} />}
            />
          </>
        }
      />
      <Route
        path="/preferences/favorites"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<Favorites />} title={t("settings.favorites")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/users"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<Users />} title={t("settings.user")} />}
            />
          </>
        }
      />
      <Route
        path="/preferences/users/:id"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<User />} title={t("settings.user")} />}
            />
          </>
        }
      />
      <Route
        path="/permissions/groups"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<Groups />} title={t("groups.groups")} />}
            />
          </>
        }
      />
      <Route
        path="/permissions/permissions/:id"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={<Permissions />}
                  title={t("settings.permissions")}
                />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/general"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<Generals />} title={t("settings.general")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/mailtemplates"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={<MailTemplates />}
                  title={t("settings.mailTemplates")}
                />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/mailarchive"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<MailArchive />} title={t("settings.mail")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/logging"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<Logging />} title={t("settings.logging")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/export"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<Export />} title={t("settings.data-export")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/payment"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={
                    <Payment />
                  }
                  title={t("settings.payments")}
                />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/referral"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={<Referral />}
                  title={t("settings.referral")}
                />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences"
        element={
          <>
            <Navigate to="/preferences/profile" />
            <AppLayout
              content={<Sidebar contentdisplay={<Profile />} title={t("settings.profile")} />}
            />
          </>
        }
      />
      <Route
        path="/preferences/profile"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<Profile />} title={t("settings.profile")} />}
            />
          </>
        }
      />
      <Route
        path="/preferences/version"
        element={
          <>
            <AppLayout
              content={<Sidebar contentdisplay={<Version />} title={t("settings.version")} />}
            />
          </>
        }
      />
      <Route
        path="/preferences/about"
        element={
          <>
            <AppLayout
              content={
                <Sidebar contentdisplay={<About />} title={t("settings.aboutSophyapp")} />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/locations"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={<Locations />}
                  title={t("settings.locations")}
                />
              }
            />
          </>
        }
      />
      <Route
        path="/preferences/security"
        element={
          <>
            <AppLayout
              content={
                <Sidebar
                  contentdisplay={<TwoFactor />}
                  title={t("settings.twoFactor", { defaultValue: "Zwei-Faktor-Authentifizierung" })}
                />
              }
            />
          </>
        }
      />
      <Route
        path="*"
        element={
          <>
            <Navigate to="/dashboard" />
            <AppLayout content={<Patients />} />
          </>
        }
      />
    </Routes>
  );
};

export default AppRoutes; 